import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useDashboardContext from 'contexts/dashboard.context';
import PageHeaderText from 'components/base/page-header-text';
import FormStepper from 'components/base/form-stepper';
import useInventoryStoreContext from 'contexts/inventory-store.context/context';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import UpdateStoreDetails from './update-store-details';
import { AddStoreItemType, StoreItemType } from 'models/store-item.model';
import { DefaultStoreItem } from 'contexts/inventory-store.context/provider';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { apiService } from 'services/base.service';
import UpdateStorePreviewItems from './bulk-edit-store-items';
import useRoleContext from 'contexts/role.context';
import { InventoryModule, RolePermission } from 'models/permission.model';

const BulkEditStoreItems = () => {
  const navigate = useNavigate();
  const { setIsBackLink, setBackLinkTitle } = useDashboardContext();
  const [step, setStep] = useState(1);
  const [item, setItem] = useState<AddStoreItemType>(DefaultStoreItem);
  const [vendorIngredientIdsToDelete, setVendorIngredientIdsToDelete] = useState<string[]>([]);
  const [isUpdatingStoreItems, setIsUpdatingStoreItems] = useState(false);
  const { can } = useRoleContext();
  const canWriteInventoryManagement = can(
    InventoryModule.InventoryManagement,
    RolePermission.Write
  );

  const { storeItemsToEdit, setStoreItemsToEdit } = useInventoryStoreContext();
  useEffect(() => {
    if (!storeItemsToEdit) {
      navigate(`/${MAIN_STORE_ROUTE_KEYS.INVENTORY_STORE_ITEMS_BULK_EDIT}`);
    }
  }, [storeItemsToEdit, navigate]);
  useEffect(() => {
    setIsBackLink(true);
    setBackLinkTitle('Return to Inventory');
    return () => {
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, []);

  function isFormFilled(data: StoreItemType[]): boolean {
    for (const item of data) {
      for (const key in item) {
        if (
          typeof item[key as keyof StoreItemType] === 'string' &&
          item[key as keyof StoreItemType] === ''
        ) {
          return false;
        }
      }
    }
    return true;
  }
  const updateStoreItems = async () => {
    if (!canWriteInventoryManagement) return;
    const data = {
      items: storeItemsToEdit.map((storeItem) => {
        const unitWeight = Number(storeItem.unit_weight);
        const vendors = storeItem.vendor_ingredients
          .filter((vendorIngredient) => !vendorIngredientIdsToDelete.includes(vendorIngredient.id))
          .map((vendorIngredient) => ({
            vendor_id: vendorIngredient.vendor_id,
            gram_price: Number(vendorIngredient.gram_price)
          }))
          .filter(
            (vendorIngredient) => vendorIngredient.vendor_id && vendorIngredient.gram_price > 0
          );

        const item: {
          id: string;
          name: string;
          category: string;
          unit_type: string;
          unit_weight?: number;
          min_quantity?: number;
          shelf_life?: number;
          vendors?: { vendor_id: string; gram_price: number }[];
        } = {
          id: storeItem.id,
          name: storeItem.name,
          category: storeItem.category,
          unit_type: storeItem.unit_type
        };

        if (unitWeight > 0) {
          item.unit_weight = unitWeight;
        }

        if (storeItem.min_quantity > 0) {
          item.min_quantity = storeItem.min_quantity;
        }

        const shelfLifeValue = storeItem.shelf_life ?? storeItem.shelfLife;
        if (typeof shelfLifeValue === 'number' && shelfLifeValue > 0) {
          item.shelf_life = shelfLifeValue;
        }

        if (vendors.length > 0) {
          item.vendors = vendors;
        }

        return item;
      })
    };
    try {
      setIsUpdatingStoreItems(true);
      const response = await apiService.put('ingredients', { version: 'v2', data });
      if (response) {
        setStoreItemsToEdit([]);

        toast(<ToastMessage message='Store item(s) updated successfully' type='success' />);
        navigate(MAIN_STORE_ROUTE_KEYS.INVENTORY_STORE_ITEMS);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error updating store items'}
          type='error'
        />
      );
    } finally {
      setIsUpdatingStoreItems(false);
    }
  };

  return (
    <div>
      <PageHeaderText title='Edit Selected items' />
      <div className='w-[400px] md:w-[670px]'>
        <FormStepper
          title=''
          subtitle=''
          noOfSteps={2}
          step={step}
          setStep={setStep}
          handleConfirmClick={() => updateStoreItems()}
          disabled={step === 1 && !isFormFilled(storeItemsToEdit)}
          hidePrimaryAction={!canWriteInventoryManagement}
          loading={isUpdatingStoreItems}
          handleCancelClick={() => {
            setStoreItemsToEdit([]);
            navigate(-1);
          }}>
          <div>
            {step === 1 ? (
              <UpdateStoreDetails
                vendorIngredientIdsToDelete={vendorIngredientIdsToDelete}
                setVendorIngredientIdsToDelete={setVendorIngredientIdsToDelete}
              />
            ) : (
              <UpdateStorePreviewItems
                setItem={setItem}
                item={item}
                vendorIngredientIdsToDelete={vendorIngredientIdsToDelete}
                setVendorIngredientIdsToDelete={setVendorIngredientIdsToDelete}
              />
            )}
          </div>
        </FormStepper>
      </div>
    </div>
  );
};

export default BulkEditStoreItems;
